import { ReactNode } from "react";
import { Card, CardContent, Typography, Tooltip } from "@altimateai/lego";
import styles from "./styles.module.scss";
import classnames from "classnames";
import { Stack } from "@altimateai/lego";
import { InfoGray } from "@assets/icons";
import { SummaryItem, SummaryRangeValue } from "./types";

/** Discriminator for the {min, max} range shape. Callers wire tooltips via
 *  the dedicated ``tooltip`` config key now (instead of inlining
 *  ``<Tooltip>{value}</Tooltip>``), so we just need the structural test:
 *  any non-null plain object that exposes both ``min`` and ``max``. A
 *  ReactElement wraps its props in ``.props``, so ``'min' in element`` is
 *  false — no extra ``isValidElement`` guard required. */
const isRangeValue = (v: SummaryItem["value"]): v is SummaryRangeValue =>
  typeof v === "object" && v !== null && !Array.isArray(v) && "min" in v && "max" in v;

interface FlexListSummaryProps {
  summary: SummaryItem[];
  isLoading?: boolean;
  skeletonCount?: number;
  className?: string;
}

const FlexListSummary = ({
  summary,
  isLoading = false,
  skeletonCount = 4,
  className = "",
}: FlexListSummaryProps) => {
  const colorList = [styles.blue, styles.orange, styles.yellow, styles.green, styles.pink];

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-0">
          <div
            className={styles.container}
            style={{ gridTemplateColumns: `repeat(${skeletonCount}, 1fr)` }}
          >
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <div key={i} className={styles.item} role="group" aria-label="Loading">
                <div className="pt-2 pb-2">
                  <div className="al-h-3 al-w-20 al-mb-2 al-rounded-md al-animate-pulse al-bg-gray-15" />
                  <div className="al-h-5 al-w-14 al-rounded-md al-animate-pulse al-bg-gray-15" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="p-0">
        <div
          className={styles.container}
          style={{ gridTemplateColumns: `repeat(${summary?.length}, 1fr)` }}
        >
          {summary.map((item, i) => {
            const renderedValue = isRangeValue(item.value)
              ? `${item.value.min} - ${item.value.max}`
              : (item.value as ReactNode);
            // Wrap the value in a Tooltip when the caller passed one.
            // Keeps the Tooltip detail out of caller code — the queries
            // page uses this to surface the un-truncated Total Queries
            // count behind the compact-formatted display.
            const valueNode = item.tooltip ? (
              <Tooltip title={item.tooltip}>
                <span>{renderedValue}</span>
              </Tooltip>
            ) : (
              renderedValue
            );
            return (
              <div key={item.label} className={styles.item} role="group" aria-label={item.label}>
                <div className={classnames(styles.border, colorList[i])} />
                <div className="pt-2 pb-2">
                  <Stack align="center" gap="xs">
                    <Typography
                      variant="body"
                      size="sm"
                      weight="medium"
                      className="al-text-gray-600"
                    >
                      {item.label}
                    </Typography>
                    {item?.info && (
                      <Tooltip title={item.info}>
                        <Stack align="center">
                          <InfoGray />
                        </Stack>
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography
                    variant="h3"
                    weight="semibold"
                    className="al-text-gray-900"
                    aria-label={`${item.label} value`}
                  >
                    {valueNode}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default FlexListSummary;
